import { useState, useEffect, useRef } from 'react';
import { getLeadSyncProgress } from '@/services/api/meta-leads';
import type { LeadSyncProgressResponse } from '@/types/meta-lead';
import {
    Loader2,
    ChevronDown,
    ChevronUp,
    X,
    CheckCircle2,
    XCircle,
    RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FloatingSyncStatusProps {
    taskId: string;
    onClose: () => void;
}

const FOLDER_LABELS: Record<string, string> = {
    inbox: 'Caixa de Entrada',
    page_done: 'Concluídas',
    spam: 'Spam',
    other: 'Outras',
};

export default function FloatingSyncStatus({ taskId, onClose }: FloatingSyncStatusProps) {
    const [progress, setProgress] = useState<LeadSyncProgressResponse | null>(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const poll = async () => {
            try {
                const data = await getLeadSyncProgress(taskId);
                setProgress(data);

                if (data.status === 'completed' || data.status === 'failed' || data.status === 'not_found') {
                    if (data.status === 'not_found') {
                        setProgress({ ...data, status: 'completed' });
                    }
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                }
            } catch (error) {
                console.error('Error polling sync progress:', error);
            }
        };

        poll();
        intervalRef.current = setInterval(poll, 3000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [taskId]);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(onClose, 300);
    };

    if (!progress) return null;

    const isCompleted = progress.status === 'completed';
    const isFailed = progress.status === 'failed';
    const isRunning = !isCompleted && !isFailed;

    const progressPercent = progress.total_pages > 0
        ? Math.round((progress.current_page_index / progress.total_pages) * 100)
        : 0;

    return (
        <div
            className={`fixed bottom-6 right-6 z-50 transition-all duration-300 transform ${isClosing ? 'translate-y-20 opacity-0' : 'translate-y-0 opacity-100'
                }`}
        >
            <div className={`bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden transition-all duration-300 ${isMinimized ? 'w-64' : 'w-80'
                }`}>
                {/* Header */}
                <div className={`px-4 py-3 flex items-center justify-between border-b border-gray-50 ${isCompleted ? 'bg-emerald-50' : isFailed ? 'bg-red-50' : 'bg-[#0f172a]'
                    }`}>
                    <div className="flex items-center gap-2">
                        {isRunning ? (
                            <RefreshCw className={`h-4 w-4 animate-spin ${isRunning ? 'text-white' : ''}`} />
                        ) : isCompleted ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                        )}
                        <span className={`text-sm font-semibold ${isRunning ? 'text-white' : 'text-gray-900'}`}>
                            {isRunning ? 'Sincronizando...' : isCompleted ? 'Sync Concluído' : 'Sync Falhou'}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setIsMinimized(!isMinimized)}
                            className={`p-1 rounded-md transition-colors ${isRunning ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100 text-gray-500'
                                }`}
                        >
                            {isMinimized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                        <button
                            onClick={handleClose}
                            className={`p-1 rounded-md transition-colors ${isRunning ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100 text-gray-500'
                                }`}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                {!isMinimized && (
                    <div className="p-4 space-y-4">
                        {/* Progress */}
                        <div className="space-y-2">
                            <div className="flex justify-between text-xs font-medium text-gray-500">
                                <span>Progresso Geral</span>
                                <span>{isCompleted ? 100 : progressPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                    className={`h-full transition-all duration-500 ${isFailed ? 'bg-red-500' : isCompleted ? 'bg-emerald-500' : 'bg-[#0f172a]'
                                        }`}
                                    style={{ width: `${isCompleted ? 100 : progressPercent}%` }}
                                />
                            </div>
                        </div>

                        {/* Status Details */}
                        {isRunning && (
                            <div className="text-xs text-gray-600 flex items-center gap-2">
                                <Loader2 className="h-3 w-3 animate-spin text-[#0f172a]" />
                                <span className="truncate max-w-[200px]">
                                    {progress.current_page || 'Iniciando...'}
                                    {progress.current_folder && ` • ${FOLDER_LABELS[progress.current_folder] || progress.current_folder}`}
                                </span>
                            </div>
                        )}

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-2">
                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                                <div className="text-lg font-bold text-[#0f172a]">{progress.leads_created}</div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Novos</div>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                                <div className="text-lg font-bold text-gray-600">{progress.leads_existing}</div>
                                <div className="text-[10px] text-gray-500 uppercase tracking-wider">Existentes</div>
                            </div>
                        </div>

                        {isFailed && (
                            <p className="text-xs text-red-600 font-medium bg-red-50 p-2 rounded-md">
                                {progress.error || 'Ocorreu um erro desconhecido.'}
                            </p>
                        )}

                        {isCompleted && (
                            <Button
                                size="sm"
                                className="w-full bg-[#0f172a] hover:bg-[#1e293b]"
                                onClick={handleClose}
                            >
                                Ok, entendi
                            </Button>
                        )}
                    </div>
                )}

                {/* Minimized Progress Bar */}
                {isMinimized && isRunning && (
                    <div className="h-1 bg-gray-100">
                        <div
                            className="h-full bg-[#0f172a] transition-all duration-500"
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
